/**
 * lrb-personalization.js — v3 Embed + Signal Edition
 * Leaps & Rebounds Customer Intelligence Snippet
 *
 * Architecture:
 *   - Sections live inside inert <template> elements in #lrb-template-pool
 *   - First ABOVE_FOLD_COUNT sections are cloned into #lrb-intelligence-inject immediately
 *   - Remaining sections use IntersectionObserver sentinels to lazy-inject 500px ahead of scroll
 *   - Zero DOM parsing cost for any section until it's actually needed
 *
 * v3 additions:
 *   - embed_id support: pass via window.LRB_INTELLIGENCE_CONFIG.embedId
 *   - UTM signal auto-detection: reads URL params, fires segment_tags to profile
 *   - first_time_user / returning signals via localStorage TTL
 *
 * DEV MODE: verbose console logging + on-page debug overlay.
 * Flip DEV = false before production.
 */

(() => {
  'use strict';

  // ── Run-once guard ───────────────────────────────────────────────────────────
  // Prevents double-execution if lrb-embed.liquid is accidentally added to the
  // Shopify template more than once (causes duplicate sections on the page).
  if (window.__lrbPersonalizationLoaded) {
    console.warn('[LRB] lrb-personalization.js loaded twice — aborting duplicate instance.');
    return;
  }
  window.__lrbPersonalizationLoaded = true;

  const QS = new URLSearchParams(window.location.search);
  const DEBUG_PANEL = QS.get('lrb_debug') === '1';
  const DEV = DEBUG_PANEL; // enables console logs + section outlines when ?lrb_debug=1

  // ── Config ──────────────────────────────────────────────────────────────────

  const CONFIG = window.LRB_INTELLIGENCE_CONFIG || {};
  const BOT_URL = CONFIG.botUrl || 'https://gravity-claw-production-fb9e.up.railway.app';
  let EMBED_ID = CONFIG.embedId || null; // null = return all active sections

  // ── The email parameter contract ─────────────────────────────────────────────
  // Owned by lrb-params.js, which is loaded ahead of this file and also applies
  // the copy to the static page. It is kept separate on purpose: offer copy must
  // survive a cold Railway container or a failed sections request, neither of
  // which this file can promise. The fallback list below only matters if that
  // asset failed to load, and must stay in step with it.
  const LRB_API = window.LRB || {};
  const PARAM_KEYS = LRB_API.PARAM_KEYS || [
    'lrb_offer',
    'lrb_offer_text',
    'lrb_angle',
    'lrb_code',
    'lrb_ends',
  ];
  const URL_PARAMS =
    LRB_API.params ||
    Object.fromEntries(PARAM_KEYS.map(k => [k, (QS.get(k) || '').trim()]).filter(([, v]) => v));

  // ?lrb_embed=<uuid> previews any embed on any page. This is what makes one
  // demo page able to stand in for every landing page in Mission Control.
  const EMBED_OVERRIDE = QS.get('lrb_embed');
  const VALID_OVERRIDE = !!EMBED_OVERRIDE && /^[0-9a-f-]{36}$/i.test(EMBED_OVERRIDE);
  if (VALID_OVERRIDE) EMBED_ID = EMBED_OVERRIDE;

  // Preview mode: an embed override, a QA session (?lrb_debug=1) or any theme
  // other than the live one (window.LRB_PREVIEW_THEME, set in layout/theme.liquid;
  // ?lrb_preview=0 opts out) is internal traffic. Sections still render, but the
  // client sends no stats, and every request that reaches the server carries
  // preview: true so gravity-claw neither counts it nor saves the profile.
  const PREVIEW_THEME = window.LRB_PREVIEW_THEME === true && QS.get('lrb_preview') !== '0';
  const PREVIEW = VALID_OVERRIDE || DEBUG_PANEL || PREVIEW_THEME;

  const COOKIE_NAME = 'lrb_vid';
  const COOKIE_DAYS = 365;
  const HOST_ID = 'lrb-intelligence-inject'; // mount point
  const POOL_ID = 'lrb-template-pool'; // inert template pool
  const ABOVE_FOLD_COUNT = 2; // sections injected immediately on load
  const MAX_SECTIONS = 50; // effectively unlimited — show all as user scrolls
  const LOOK_AHEAD_PX = 600; // px below viewport to trigger next injection
  // The parameters are part of the cache identity. Without them, a visitor who
  // opens ?lrb_offer=storecredit and then ?lrb_offer=35off inside the TTL is
  // served the first stack for the second offer.
  const PARAM_SIGNATURE = PARAM_KEYS.map(k => URL_PARAMS[k] || '').join('|');
  let SECTION_CACHE_KEY = `lrb_ranked_${EMBED_ID || 'default'}_${window.location.pathname}${
    /^\|*$/.test(PARAM_SIGNATURE) ? '' : `_${PARAM_SIGNATURE}`
  }`;
  const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
  const API_TIMEOUT_MS = 8000; // 8s — gives Railway time even behind Puppeteer's request intercept
  const FIRST_SEEN_KEY = 'lrb_first_seen'; // localStorage — ms timestamp of first visit
  const FIRST_USER_TTL = 24 * 60 * 60 * 1000; // 24h

  // ── Session-level impressed sections ────────────────────────────────────────
  // Accumulates every section that receives an impression this page session.
  // Each entry: { shopifyId: string, sectionId: string|null }
  //   shopifyId  — the snippet ID used by /page/event (writes personalization_sections.stats)
  //   sectionId  — the UUID used by PATCH /embeds/:id/stats (writes embed_sections.add_to_carts)
  // The document-level ATC listener uses this list to attribute conversions
  // to all sections the visitor actually saw — since the Shopify native ATC
  // button lives outside our injected section elements.
  const _impressedSections = [];
  // Filled from the ranked response: the embed the server resolved (a landing
  // page on the shared template names none itself) and the variation each
  // section was served as. Without them a beacon has nothing to credit — a
  // path-resolved embed recorded no add-to-carts, and no variation ever
  // recorded one.
  let _servedEmbedId = null;
  const _servedVariation = {}; // section UUID → variation UUID
  const _clickedSections = {}; // section UUID → true, once per page view

  // Best-effort JSON beacon. keepalive lets it outlive a navigation.
  function postJSON(path, payload, method = 'POST') {
    return fetch(BOT_URL + path, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  }

  function embedStat(sectionId, event) {
    const embedId = EMBED_ID || _servedEmbedId;
    if (!embedId || !sectionId) return false;
    if (PREVIEW) {
      log('Preview — embed stat not sent:', event, sectionId);
      return false;
    }
    postJSON(
      `/admin/intelligence/embeds/${embedId}/stats`,
      { section_id: sectionId, event, variation_id: _servedVariation[sectionId] || undefined },
      'PATCH'
    ).catch(() => {
      /* non-critical — best-effort */
    });
    return true;
  }
  let _atcListenerAttached = false;
  let _atcDebounceTimer = null;
  let _atcFired = false;

  // A function declaration at the IIFE's top level, so it is hoisted: both
  // installFetchIntercept (runs immediately) and the attachTracking listeners
  // can call it. Strict mode makes function declarations inside blocks
  // block-scoped, so this MUST stay at the top level.
  function _fireAtcForAllSections() {
    if (_atcFired) return; // debounce — one report per ATC action
    _atcFired = true;
    clearTimeout(_atcDebounceTimer);
    _atcDebounceTimer = setTimeout(() => {
      _atcFired = false;
    }, 600);
    const entries = _impressedSections.slice();
    if (!entries.length) return; // no sections impressed yet — nothing to attribute
    // The emoji and dashes in these messages are \u escapes, as they always were
    // here: they survive however the file ends up decoded.
    const shopifyIds = entries.map(e => e.shopifyId);
    log(
      '\uD83D\uDED2 ATC detected \u2014 attributing to',
      entries.length,
      'impressed section(s):',
      shopifyIds
    );
    dbg(`\uD83D\uDED2 ATC \u2192 ${shopifyIds.join(', ')}`);

    // 1. Write to personalization_sections.stats.add_to_cart (profile-level analytics)
    for (const e of entries) reportEvent('add_to_cart', e.shopifyId);

    // 2. Write to embed_sections.add_to_carts (UCB1 embed-level counter — powers AATC% in Mission Control)
    // Fallback-injected sections have no UUID and are skipped inside embedStat.
    const pinged = entries.filter(e => embedStat(e.sectionId, 'add_to_cart')).length;
    if (pinged)
      log(
        '\uD83D\uDED2 Embed ATC ping sent for',
        pinged,
        'section(s) on embed',
        EMBED_ID || _servedEmbedId
      );
  }

  // ── fetch() intercept — page-level ATC capture ───────────────────────────────
  // Intercepts every /cart/add.js fetch — the ONE network call all Shopify
  // themes make for ATC regardless of form submit, ajax, or custom events.
  try {
    const origFetch = window.fetch;
    // A plain function, not an arrow: it is called as window.fetch, and passes
    // its own `this` and its arguments, untouched, on to the real fetch.
    window.fetch = function (...args) {
      const input = args[0];
      const url = typeof input === 'string' ? input : input?.url || '';
      const result = origFetch.apply(this, args);
      if (url.includes('/cart/add')) {
        result
          .then(response => {
            if (response.ok) _fireAtcForAllSections();
          })
          .catch(() => {
            /* noop */
          });
      }
      return result;
    };
  } catch {
    /* fetch not patchable — form/event listeners remain as fallback */
  }

  // ── Preconnect hint ──────────────────────────────────────────────────────────

  try {
    const host = new URL(BOT_URL).origin;
    if (!document.querySelector(`link[rel="preconnect"][href="${host}"]`)) {
      const pc = document.createElement('link');
      pc.rel = 'preconnect';
      pc.href = host;
      pc.crossOrigin = 'anonymous';
      document.head.appendChild(pc);
      const dns = document.createElement('link');
      dns.rel = 'dns-prefetch';
      dns.href = host;
      document.head.appendChild(dns);
    }
  } catch {
    /* noop */
  }

  // ── Dev helpers ─────────────────────────────────────────────────────────────

  function log(...args) {
    if (DEV) console.log('[LRB 🧠]', ...args);
  }

  function err(...args) {
    if (DEV) console.error('[LRB ❌]', ...args);
  }

  let overlay;
  function dbg(msg) {
    if (!DEV) return;
    log(msg);
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'lrb-debug-overlay';
      overlay.style.cssText = [
        'position:fixed',
        'bottom:12px',
        'right:12px',
        'z-index:99999',
        'background:rgba(0,0,0,0.85)',
        'color:#4ade80',
        'font-family:monospace',
        'font-size:11px',
        'padding:10px 14px',
        'border-radius:8px',
        'max-width:380px',
        'max-height:280px',
        'overflow-y:auto',
        'border:1px solid rgba(74,222,128,0.3)',
        'line-height:1.6',
        'pointer-events:none',
      ].join(';');
      document.body.appendChild(overlay);
    }
    const line = document.createElement('div');
    line.textContent = `▸ ${msg}`;
    overlay.appendChild(line);
    overlay.scrollTop = overlay.scrollHeight;
  }

  // ── Storage helpers (every access can throw: private mode, blocked site data) ─

  function sessionGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }
  function sessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  }
  function sessionRemove(key) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  // ── Cookie helpers ───────────────────────────────────────────────────────────

  function getCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  // ── Visitor ID ───────────────────────────────────────────────────────────────

  let visitorId = getCookie(COOKIE_NAME);
  if (!visitorId) {
    visitorId = uuid();
    setCookie(COOKIE_NAME, visitorId, COOKIE_DAYS);
    log('New visitor ID created:', visitorId);
  } else {
    log('Returning visitor ID:', visitorId);
  }

  // ── Signal extraction ────────────────────────────────────────────────────────

  // The query-string values present for these keys, as an object.
  function pickParams(keys) {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    for (const k of keys) if (params.get(k)) out[k] = params.get(k);
    return out;
  }

  function referrerHost() {
    if (!document.referrer) return '';
    try {
      return new URL(document.referrer).hostname;
    } catch {
      return document.referrer;
    }
  }

  function getPageSignals() {
    return {
      page_type: (document.body.className.match(/template-(\w+)/) || [])[1] || 'unknown',
      referrer: referrerHost(),
      url: window.location.pathname,
      ...pickParams(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']),
    };
  }

  function getAdSignals() {
    const ad = pickParams(['utm_source', 'utm_medium', 'utm_campaign', 'fbclid', 'gclid']);
    return Object.keys(ad).length ? ad : null;
  }

  // ── Auto-signal detection ────────────────────────────────────────────────────
  // Derives segment_tags from URL params & localStorage state.
  // These are merged into the profile upsert — no separate API call.

  function deriveAutoSignals() {
    const tags = [];
    const params = new URLSearchParams(window.location.search);

    // ── UTM-based signals ────────────────────────────────────────────────────
    // Marketing team encodes intent directly in UTM campaign values
    const campaign = (params.get('utm_campaign') || '').toLowerCase();
    const source = (params.get('utm_source') || '').toLowerCase();
    const inCampaign = (...words) => words.some(w => campaign.includes(w));

    if (inCampaign('knee', 'joint')) tags.push('knee_pain');
    if (inCampaign('back')) tags.push('back_pain');
    if (inCampaign('shoulder')) tags.push('shoulder_pain');
    if (inCampaign('senior', '50')) tags.push('senior');
    if (inCampaign('athletic', 'performance')) tags.push('athlete');
    if (inCampaign('wellness', 'pain_relief')) tags.push('pain_relief');
    if (source === 'facebook' || source === 'instagram' || params.get('fbclid'))
      tags.push('facebook_ad');
    if (source === 'google' || params.get('gclid')) tags.push('google_ad');

    // ── Page-view signals ────────────────────────────────────────────────────
    const pathname = window.location.pathname.toLowerCase();
    const inPath = (...parts) => parts.some(p => pathname.includes(p));
    if (inPath('/blogs/joint', '/blogs/knee')) tags.push('pain_relief');
    if (inPath('/blogs/performance', '/blogs/hiit')) tags.push('athlete');
    if (inPath('/blogs/senior', '/blogs/mobility')) tags.push('senior');

    // ── Time-based signals ───────────────────────────────────────────────────
    const now = Date.now();
    try {
      const firstSeen = localStorage.getItem(FIRST_SEEN_KEY);
      if (!firstSeen) {
        // Brand new visitor — set first_seen
        localStorage.setItem(FIRST_SEEN_KEY, String(now));
        tags.push('first_time_user');
        log('Signal: first_time_user (new)');
        dbg('Signal: first_time_user');
      } else {
        const age = now - parseInt(firstSeen, 10);
        if (age < FIRST_USER_TTL) {
          // Still within 24h window
          tags.push('first_time_user');
          log('Signal: first_time_user (still within 24h, age:', `${Math.round(age / 60000)}min)`);
        } else {
          // Expired — they're a returning visitor
          tags.push('returning');
          log('Signal: returning (first seen', `${Math.round(age / 86400000)}d ago)`);
          dbg('Signal: returning');
        }
      }
    } catch {
      /* localStorage blocked */
    }

    // ── Mission Control signals ──────────────────────────────────────────────
    // Everything defined under Website → Signals, earned on this page or any
    // earlier one — applied and remembered by lrb-signals.js.
    if (window.LRB_SIGNALS) tags.push(...window.LRB_SIGNALS.tags());

    if (tags.length) {
      log('Auto-signals derived:', tags);
      dbg(`Signals: ${tags.join(', ')}`);
    }
    return tags.length ? [...new Set(tags)] : null;
  }

  // ── Fire-and-forget profile upsert ───────────────────────────────────────────

  function upsertProfile(extra) {
    const send = async () => {
      const autoTags = deriveAutoSignals();
      const payload = { visitor_id: visitorId, page_signals: getPageSignals(), ...extra };
      const adSignals = getAdSignals();
      if (adSignals) payload.ad_signals = adSignals;
      // Attach auto-derived segment_tags (merged server-side with existing tags)
      if (autoTags) payload.segment_tags = autoTags;
      if (PREVIEW) payload.preview = true;

      log('Profile upsert →', `${BOT_URL}/admin/intelligence/profile`, payload);
      dbg(`Profile signal sent (${payload.page_signals.page_type})`);
      try {
        const r = await postJSON('/admin/intelligence/profile', payload);
        log('Profile upsert ok:', await r.json());
      } catch (e) {
        err('Profile upsert failed:', e.message);
      }
    };
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(send, { timeout: 2000 });
    } else {
      setTimeout(send, 300);
    }
  }

  // ── Event reporting ──────────────────────────────────────────────────────────

  function reportEvent(eventType, sectionId, extra) {
    const payload = {
      visitor_id: visitorId,
      section_shopify_id: sectionId || null,
      event_type: eventType,
      ...extra,
    };
    if (PREVIEW) {
      log('Preview — event not sent:', eventType, sectionId || '', extra || '');
      return;
    }
    log('Event:', eventType, sectionId || '', extra || '');
    postJSON('/admin/intelligence/page/event', payload).catch(e =>
      err('Event report failed:', e.message)
    );
  }

  // ── Page hidden: flush dwell and scroll depth ───────────────────────────────
  // Both are sent when the page is hidden, which covers a tab switch, an app
  // backgrounded, a navigation and a close. visibilitychange catches mobile,
  // where a backgrounded tab is often discarded without any later event;
  // pagehide catches the rest. beforeunload is not used: mobile browsers often
  // skip it, and some browsers keep a page with one out of the back/forward cache.

  const _dwellTimers = []; // one per tracked section: { shopifyId, inView, start }
  let _maxScrollPct = null; // null until applyRankedSections starts tracking it
  let _scrollDepthSent = false;

  function endDwell(t) {
    if (!t.start) return;
    const ms = Date.now() - t.start;
    if (ms > 500) reportEvent('dwell', t.shopifyId, { dwell_ms: ms });
    t.start = null;
  }

  function onPageHidden() {
    for (const t of _dwellTimers) endDwell(t);
    // Once per page view, at the first hide, as beforeunload sent it before: a
    // tab switched away and back must not send a second one.
    if (!_scrollDepthSent && _maxScrollPct > 10) {
      _scrollDepthSent = true;
      reportEvent('scroll_depth', null, { scroll_pct: _maxScrollPct });
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      onPageHidden();
      return;
    }
    // Back on screen: sections still in view start a new dwell. The observer
    // doesn't fire again for them, since their intersection didn't change.
    const now = Date.now();
    for (const t of _dwellTimers) if (t.inView && !t.start) t.start = now;
  });
  window.addEventListener('pagehide', onPageHidden);

  // ── Inline script re-execution ──────────────────────────────────────────────

  function reExecScripts(wrapper) {
    for (const orig of wrapper.querySelectorAll('script')) {
      const s = document.createElement('script');
      for (const a of orig.attributes) s.setAttribute(a.name, a.value);
      s.textContent = orig.textContent;
      orig.replaceWith(s);
    }
  }

  // ── Section entrance animations ──────────────────────────────────────────────
  // Injected once; two variants:
  //   lrb-reveal-immediate — above-fold (fast, subtle — section was always "there")
  //   lrb-reveal-lazy      — below-fold (slightly slower + more travel — arriving on scroll)

  let _animStylesInjected = false;
  function injectAnimationStyles() {
    if (_animStylesInjected) return;
    _animStylesInjected = true;
    const s = document.createElement('style');
    s.textContent = [
      '@keyframes lrb-fade-up-fast{',
      'from{opacity:0;transform:translateY(16px)}',
      'to{opacity:1;transform:translateY(0)}',
      '}',
      '@keyframes lrb-fade-up-lazy{',
      'from{opacity:0;transform:translateY(28px)}',
      'to{opacity:1;transform:translateY(0)}',
      '}',
      '.lrb-reveal-immediate{',
      'animation:lrb-fade-up-fast 0.42s cubic-bezier(0.22,1,0.36,1) both;',
      '}',
      '.lrb-reveal-lazy{',
      'animation:lrb-fade-up-lazy 0.54s cubic-bezier(0.22,1,0.36,1) both;',
      '}',
      // Respect reduced-motion preference
      '@media(prefers-reduced-motion:reduce){',
      '.lrb-reveal-immediate,.lrb-reveal-lazy{',
      'animation:none;opacity:1;transform:none;',
      '}',
      '}',
    ].join('');
    document.head.appendChild(s);
  }

  // ── Template cloning ─────────────────────────────────────────────────────────

  function cloneTemplate(pool, sectionId) {
    const tmpl = pool.querySelector(`template[data-lrb-id="${sectionId}"]`);
    if (!tmpl) {
      err('Template not found for section:', sectionId, '— deploy the snippet first');
      dbg(`No template for: ${sectionId}`);
      return null;
    }
    const wrapper = document.createElement('div');
    wrapper.setAttribute('data-lrb-id', sectionId);
    wrapper.appendChild(tmpl.content.cloneNode(true));
    // Before the scripts run, so a section's own JS sees the final copy.
    LRB_API.applyOfferCopy?.(wrapper);
    reExecScripts(wrapper);
    return wrapper;
  }

  // ── Tracking attachment ───────────────────────────────────────────────────────

  // Attached once per page load, on the first section that is tracked.
  // Shopify's native ATC button ([name="add"]) lives in main-product.liquid,
  // completely outside our injected section elements — per-element listeners
  // would never fire for it. One document listener catches all paths:
  //   - Standard form submit (name="add" button click)
  //   - Theme JS that dispatches a custom "cart:add" or "theme:cart:add" event
  //   - Shopify ajax cart (catches fetch/XHR to /cart/add.js via the custom event
  //     Shopify themes fire on window after a successful add)
  function attachAtcListeners() {
    if (_atcListenerAttached) return;
    _atcListenerAttached = true;

    // 1. Native form submit
    document.addEventListener(
      'submit',
      e => {
        const form = e.target;
        if (!form || form.nodeName !== 'FORM') return;
        if ((form.action || '').includes('/cart/add')) _fireAtcForAllSections();
      },
      { passive: true, capture: true }
    );

    // 2. Custom events fired by modern Shopify themes after ajax ATC
    for (const evName of [
      'cart:item:added',
      'cart:add',
      'theme:cart:add',
      'ajax:cart:add',
      'product:added',
    ]) {
      window.addEventListener(evName, _fireAtcForAllSections, { passive: true });
      document.addEventListener(evName, _fireAtcForAllSections, { passive: true });
    }

    // 3. Button click fallback
    document.addEventListener(
      'click',
      e => {
        if (
          e.target?.closest?.(
            '[name="add"], .btn-add-to-cart, [data-action="add-to-cart"], [data-add-to-cart]'
          )
        ) {
          _fireAtcForAllSections();
        }
      },
      { passive: true }
    );
  }

  // attachTracking(el, shopifyId, sectionId)
  //   shopifyId  — snippet ID string (used for /page/event calls)
  //   sectionId  — DB UUID (used for PATCH /embeds/:id/stats ATC ping); may be null for fallback injections
  function attachTracking(el, shopifyId, sectionId) {
    if (DEV) {
      el.style.outline = '2px dashed rgba(74,222,128,0.35)';
      el.style.outlineOffset = '-2px';
    }

    // Record impression (writes to personalization_sections.stats)
    reportEvent('impression', shopifyId);

    // Track for session-level ATC attribution — push only if not already present
    if (!_impressedSections.some(e => e.shopifyId === shopifyId)) {
      _impressedSections.push({ shopifyId, sectionId: sectionId || null });
    }

    attachAtcListeners();

    // Dwell tracking — measures time section is ≥50% in viewport. A view still
    // open when the page is hidden is sent by onPageHidden().
    const dwell = { shopifyId, inView: false, start: null };
    _dwellTimers.push(dwell);
    const dwellObs = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          dwell.inView = entry.isIntersecting;
          if (!entry.isIntersecting) endDwell(dwell);
          else if (!dwell.start && document.visibilityState === 'visible') dwell.start = Date.now();
        }
      },
      { threshold: 0.5 }
    );
    dwellObs.observe(el);

    // Click tracking — only fires on meaningful CTA interactions, NOT internal
    // carousel controls, tabs, accordions, or image clicks. Elements that should
    // NOT trigger a CTR count must have data-lrb-internal attribute.
    el.addEventListener(
      'click',
      e => {
        const target = e.target;
        if (!target?.closest) return;
        // Skip elements explicitly marked as internal controls
        if (target.closest('[data-lrb-internal]')) return;
        // Only count clicks on outbound-intent elements
        const isCta = target.closest(
          'a[href]:not([href="#"]):not([href=""]), ' +
            'button:not([data-lrb-internal]), ' +
            '[data-lrb-cta]'
        );
        if (!isCta) return;
        reportEvent('click', shopifyId);
        // The embed-level click is what a click-optimised embed (a blog banner) is
        // ranked on, so it is counted once per section per page view: a visitor who
        // taps two buttons in one CTA has clicked through once, not twice.
        if (sectionId && !_clickedSections[sectionId]) {
          _clickedSections[sectionId] = true;
          if (embedStat(sectionId, 'click')) log('Embed click ping sent for', shopifyId);
        }
      },
      { passive: true }
    );
  }

  // ── Immediate injection ───────────────────────────────────────────────────────

  // injectNow(host, pool, shopifyId, sectionId)
  //   shopifyId — snippet/template ID string
  //   sectionId — DB UUID (optional; undefined for fallback injections)
  function injectNow(host, pool, shopifyId, sectionId) {
    if (host.querySelector(`[data-lrb-id="${shopifyId}"]`)) {
      log('Already injected, skipping:', shopifyId);
      return;
    }
    const el = cloneTemplate(pool, shopifyId);
    if (!el) return;
    injectAnimationStyles();
    el.classList.add('lrb-reveal-immediate');
    host.appendChild(el);
    attachTracking(el, shopifyId, sectionId || null);
    log('⚡ Injected immediately:', shopifyId);
    dbg(`⚡ ${shopifyId}`);
  }

  // ── Sentinel-based lazy injection ────────────────────────────────────────────

  // setupLazyInjection(host, pool, sections)
  //   sections — array of { shopifyId, sectionId } objects
  function setupLazyInjection(host, pool, sections) {
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const sentinel = entry.target;
          const shopifyId = sentinel.getAttribute('data-lrb-sentinel');
          const sectionId = sentinel.getAttribute('data-lrb-section-id') || null;

          log('👁 Sentinel triggered for:', shopifyId);
          dbg(`Lazy inject: ${shopifyId}`);

          const el = cloneTemplate(pool, shopifyId);
          if (el) {
            injectAnimationStyles();
            el.classList.add('lrb-reveal-lazy');
            host.replaceChild(el, sentinel);
            attachTracking(el, shopifyId, sectionId);
            log('✅ Lazy-injected:', shopifyId);
          } else {
            host.removeChild(sentinel);
          }

          observer.unobserve(sentinel);
        }
      },
      {
        rootMargin: `0px 0px ${LOOK_AHEAD_PX}px 0px`,
        threshold: 0,
      }
    );

    for (const s of sections) {
      const sentinel = document.createElement('div');
      sentinel.setAttribute('data-lrb-sentinel', s.shopifyId);
      if (s.sectionId) sentinel.setAttribute('data-lrb-section-id', s.sectionId);
      sentinel.style.cssText = 'height:200px;width:100%;';
      host.appendChild(sentinel);
      observer.observe(sentinel);
      log('📍 Sentinel placed for:', s.shopifyId);
    }
  }

  // ── Cold-start fallback injection ─────────────────────────────────────────────
  // Called when the /page/sections API fails or times out (e.g. Railway cold-start).
  // Injects the first ABOVE_FOLD_COUNT templates from the pool alphabetically so the
  // page is never left blank — far better than an empty personalization zone.
  // Once the visitor's next page load hits a warm Railway instance the real ranked
  // sections take over (sessionStorage cache will be empty for this session).

  function injectFallback(host, pool) {
    // Only run if nothing has already been injected
    if (host.querySelector('[data-lrb-id]')) return;

    // Prefer user-configured fallback list, otherwise use the pool order
    const fallbackIds = CONFIG.fallbackSections?.length
      ? CONFIG.fallbackSections.slice(0, ABOVE_FOLD_COUNT)
      : [...pool.querySelectorAll('template[data-lrb-id]')]
          .slice(0, ABOVE_FOLD_COUNT)
          .map(t => t.getAttribute('data-lrb-id'));

    if (!fallbackIds.length) return;

    log('⚠️ API failed — injecting', fallbackIds.length, 'fallback section(s):', fallbackIds);
    dbg(`⚠️ Fallback: ${fallbackIds.join(', ')}`);

    // no sectionId UUID available for fallback injections
    for (const id of fallbackIds) injectNow(host, pool, id, null);
  }

  // ── Main ─────────────────────────────────────────────────────────────────────

  // A cache hit skips POST /page/sections, so the server-side
  // increment_embed_impressions / increment_variation_impressions RPCs never
  // fire. Ping the lightweight impression endpoint so UCB1 stats stay accurate
  // even for multi-page sessions within the cache window. A landing page on the
  // shared template has no embed id of its own — the server resolved it from
  // the path and echoed it back into the cached envelope, so use that when the
  // page did not name one.
  function pingCachedImpressions(data) {
    const embedId = EMBED_ID || data.embed_id;
    if (PREVIEW) {
      log('Preview — cache-hit impression ping not sent');
      return;
    }
    if (!embedId || !data.sections?.length) return;
    const sectionIds = data.sections.map(s => s.section_id).filter(Boolean);
    const variationIds = data.sections.map(s => s.variation_id).filter(Boolean);
    if (!sectionIds.length) return;
    postJSON('/admin/intelligence/page/sections/impression', {
      embed_id: embedId,
      section_ids: sectionIds,
      variation_ids: variationIds,
    }).catch(() => {
      /* non-critical — best-effort */
    });
    log('📊 Cache-hit impression ping sent for', sectionIds.length, 'sections');
    dbg(`📊 Cache impression ping → ${sectionIds.length} sections`);
  }

  // Serves the ranked stack from this session's cache. Returns true on a hit;
  // an expired or unreadable entry is removed and false is returned.
  function useCachedSections(host, pool) {
    const cachedRaw = sessionGet(SECTION_CACHE_KEY);
    if (!cachedRaw) return false;
    try {
      const envelope = JSON.parse(cachedRaw);
      const age = Date.now() - (envelope.ts || 0);
      if (age < CACHE_TTL_MS && envelope.data) {
        const secs = Math.round(age / 1000);
        log(
          `⚡ Using sessionStorage cache (${secs}s old):`,
          envelope.data.sections.length,
          'sections'
        );
        dbg(`⚡ Cache hit (${secs}s old) — skipping API`);
        applyRankedSections(host, pool, envelope.data);
        pingCachedImpressions(envelope.data);
        return true;
      }
      log(`Cache expired (${Math.round(age / 1000)}s old) — fetching fresh`);
      dbg('Cache expired — fetching fresh rankings');
    } catch {
      /* unreadable: dropped below */
    }
    sessionRemove(SECTION_CACHE_KEY);
    return false;
  }

  async function fetchRankedSections(host, pool) {
    const sectionsUrl = `${BOT_URL}/admin/intelligence/page/sections`;
    log('Requesting ranked sections from', sectionsUrl);

    const body = { visitor_id: visitorId, path: window.location.pathname };
    if (EMBED_ID) body.embed_id = EMBED_ID;
    if (PREVIEW) body.preview = true;

    // Send the parameters and the derived tags INLINE rather than relying on the
    // profile row. upsertProfile() above is queued at requestIdleCallback, so on
    // a first click from an email the row does not exist yet when this request
    // lands — scoring against the DB alone would miss every visitor we are
    // actually targeting.
    if (Object.keys(URL_PARAMS).length) body.url_params = URL_PARAMS;
    const inlineTags = deriveAutoSignals();
    if (inlineTags) body.segment_tags = inlineTags;

    try {
      const r = await fetch(sectionsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(API_TIMEOUT_MS),
      });
      log('Sections API:', r.status);
      // A non-OK response has no ranking in it, and the empty stack
      // substituted here is a stand-in, not an answer. Mark it so the cache
      // write below skips it — see the degraded check there.
      const data = r.ok ? await r.json() : { sections: [], degraded: true };

      // ── Never cache a degraded answer ──────────────────────────────────
      // This cache lasts 10 minutes and the hit branch above returns before
      // any fetch, so a stored blip outlives itself by an order of magnitude:
      // the server's own 60s TTL expiring and Supabase recovering both change
      // nothing for a visitor whose session already holds `{sections: []}`.
      // They get pool defaults on every pageview of this path and fire no
      // impressions, so UCB1 records nothing for the session. The route sets
      // `degraded` when one of its reads failed; a 5xx sets it above.
      if (!data.degraded) {
        sessionSet(SECTION_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
      } else {
        log('⚠️ Degraded sections response — not caching, will retry next pageview');
        dbg('⚠️ Degraded response — cache skipped');
      }
      applyRankedSections(host, pool, data);
    } catch (e) {
      err('Sections API failed:', e.message);
      dbg(`Sections API failed: ${e.message}`);
      // ── Fallback: inject first ABOVE_FOLD_COUNT templates from the pool so the page
      // is never blank on a Railway cold-start or network error.
      injectFallback(host, pool);
    }
  }

  function run() {
    log('=== LRB Personalization v3 (embed + signal edition) ===');
    log('BOT_URL:', BOT_URL);
    log('Visitor ID:', visitorId);
    if (EMBED_ID) log('Embed ID:', EMBED_ID);
    if (Object.keys(URL_PARAMS).length) log('URL params:', URL_PARAMS);

    const host = document.getElementById(HOST_ID);
    const pool = document.getElementById(POOL_ID);

    if (!host) {
      err(`No #${HOST_ID} found — section will not inject.`);
      dbg(`ERROR: #${HOST_ID} missing`);
      upsertProfile();
      return;
    }
    if (!pool) {
      err(`No #${POOL_ID} found — templates missing.`);
      dbg(`ERROR: #${POOL_ID} missing`);
      upsertProfile();
      return;
    }

    const templateCount = pool.querySelectorAll('template[data-lrb-id]').length;
    log('Templates in pool:', templateCount);
    dbg(`Template pool: ${templateCount} snippets ready`);

    // Step 1: fire profile upsert (idle — non-blocking, includes auto-signals)
    upsertProfile();

    // Step 2: get ranked sections (with sessionStorage cache)
    if (useCachedSections(host, pool)) return;
    fetchRankedSections(host, pool);
  }

  // ── Apply ranked sections ─────────────────────────────────────────────────────

  function applyRankedSections(host, pool, data) {
    if (data.embed_id) _servedEmbedId = data.embed_id;
    for (const s of data.sections || []) {
      if (s.section_id && s.variation_id) _servedVariation[s.section_id] = s.variation_id;
    }

    // Filter out sections with no shopify_section_id (misconfigured rows), then
    // deduplicate by shopify_section_id — stale cache can produce repeated entries
    const seen = new Set();
    const ranked = (data.sections || []).slice(0, MAX_SECTIONS).filter(s => {
      if (!s.shopify_section_id || seen.has(s.shopify_section_id)) return false;
      seen.add(s.shopify_section_id);
      return true;
    });
    const ids = list => list.map(s => s.shopify_section_id);

    log(
      'Ranked sections:',
      ranked.length,
      ranked.map(s => s.shopify_section_id + (s.variation_name ? ` [${s.variation_name}]` : ''))
    );
    dbg(`Ranked: ${ids(ranked).join(', ')}`);

    if (!ranked.length) {
      dbg('No ranked sections — falling back to pool defaults');
      log('⚠️ No sections returned — calling injectFallback');
      injectFallback(host, pool);
      return;
    }

    const aboveFold = ranked.slice(0, ABOVE_FOLD_COUNT);
    const belowFold = ranked.slice(ABOVE_FOLD_COUNT);

    log('Above fold:', ids(aboveFold));
    log('Below fold (lazy):', ids(belowFold));
    dbg(`Above fold: ${aboveFold.length}  |  Lazy: ${belowFold.length}`);

    // Inject using shopify_section_id — matches template[data-lrb-id] in the pool.
    // Pass section_id UUID so ATC events can write to embed_sections.add_to_carts.
    for (const s of aboveFold) injectNow(host, pool, s.shopify_section_id, s.section_id || null);

    if (belowFold.length) {
      setupLazyInjection(
        host,
        pool,
        belowFold.map(s => ({ shopifyId: s.shopify_section_id, sectionId: s.section_id || null }))
      );
    }

    dbg(`Done — ${aboveFold.length} immediate, ${belowFold.length} lazy`);

    // Sent by onPageHidden().
    _maxScrollPct = 0;
    window.addEventListener(
      'scroll',
      () => {
        const pct = Math.round(
          ((window.scrollY + window.innerHeight) / document.body.scrollHeight) * 100
        );
        if (pct > _maxScrollPct) _maxScrollPct = pct;
      },
      { passive: true }
    );
  }

  // ── QA Signal Debug Panel ─────────────────────────────────────────────────
  // Enabled via ?lrb_debug=1 in the URL.
  // Lets you pick signals, apply them as a profile override, and re-render sections.

  function buildDebugPanel() {
    if (!DEBUG_PANEL) return;

    const SIGNALS = {
      pain: [
        { key: 'knee', label: '🦵 Knee Pain' },
        { key: 'back', label: '🔙 Back Pain' },
        { key: 'shoulder', label: '💪 Shoulder' },
        { key: 'joint', label: '🦴 Joint Pain' },
        { key: 'hip', label: '🦴 Hip Pain' },
      ],
      life_stage: [
        { key: 'senior', label: '👴 Senior (50+)' },
        { key: 'athlete', label: '🏃 Athlete' },
        { key: 'post_injury', label: '🩹 Post Injury' },
        { key: 'parent', label: '👨‍👧 Parent' },
        { key: 'general', label: '🙍 General' },
      ],
      motivation: [
        { key: 'performance', label: '⚡ Performance' },
        { key: 'fitness', label: '💪 Fitness' },
        { key: 'wellness', label: '🌿 Wellness' },
        { key: 'recovery', label: '🧘 Recovery' },
        { key: 'weight_loss', label: '⚖️ Weight Loss' },
      ],
      tags: [
        { key: 'first_time_user', label: '👋 First Visit' },
        { key: 'returning', label: '🔄 Returning' },
        { key: 'cart_abandoner', label: '🛒 Cart Abandon' },
        { key: 'purchased', label: '✅ Purchased' },
        { key: 'knee_pain', label: '🦵 knee_pain' },
        { key: 'pain_relief', label: '💊 pain_relief' },
        { key: 'facebook_ad', label: '📘 facebook_ad' },
        { key: 'google_ad', label: '🔍 google_ad' },
      ],
    };

    // Panel state
    const emptyState = () => ({ pain: {}, life_stage: null, motivation: null, tags: {} });
    let state = emptyState();

    // Inject styles
    const style = document.createElement('style');
    style.textContent = [
      '#lrb-qa-panel{position:fixed;bottom:16px;left:16px;z-index:99998;',
      'width:290px;background:rgba(10,12,20,0.96);border:1px solid rgba(56,189,248,0.25);',
      'border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,0.5);font-family:monospace;',
      'font-size:12px;color:#e2e8f0;overflow:hidden;transition:height 0.2s;}',

      '#lrb-qa-header{display:flex;align-items:center;justify-content:space-between;',
      'padding:10px 14px;background:rgba(56,189,248,0.1);border-bottom:1px solid rgba(56,189,248,0.15);cursor:pointer;}',
      '#lrb-qa-header span{font-weight:700;color:#38bdf8;letter-spacing:0.04em;font-size:11px;}',
      '#lrb-qa-toggle{background:none;border:none;color:#475569;cursor:pointer;font-size:14px;line-height:1;}',

      '#lrb-qa-body{padding:12px 14px;max-height:420px;overflow-y:auto;}',
      '.lrb-qa-group{margin-bottom:12px;}',
      '.lrb-qa-group-label{font-size:9px;text-transform:uppercase;letter-spacing:0.1em;',
      'color:#64748b;font-weight:700;margin-bottom:6px;display:block;}',
      '.lrb-qa-chips{display:flex;flex-wrap:wrap;gap:4px;}',
      '.lrb-qa-chip{padding:3px 8px;border-radius:20px;border:1px solid rgba(255,255,255,0.1);',
      'background:rgba(255,255,255,0.04);color:#94a3b8;cursor:pointer;font-size:10px;',
      'transition:all 0.15s;user-select:none;}',
      '.lrb-qa-chip:hover{border-color:rgba(56,189,248,0.4);color:#38bdf8;}',
      '.lrb-qa-chip.active{background:rgba(56,189,248,0.15);border-color:#38bdf8;color:#38bdf8;font-weight:700;}',

      '#lrb-qa-actions{display:flex;gap:6px;margin-top:10px;}',
      '#lrb-qa-apply{flex:1;padding:7px;border-radius:7px;border:none;',
      'background:rgba(56,189,248,0.2);color:#38bdf8;font-weight:700;font-size:11px;',
      'cursor:pointer;letter-spacing:0.04em;transition:background 0.15s;}',
      '#lrb-qa-apply:hover{background:rgba(56,189,248,0.35);}',
      '#lrb-qa-reset{padding:7px 10px;border-radius:7px;border:1px solid rgba(255,255,255,0.08);',
      'background:none;color:#475569;font-size:11px;cursor:pointer;}',
      '#lrb-qa-result{margin-top:10px;font-size:10px;color:#64748b;line-height:1.6;',
      'border-top:1px solid rgba(255,255,255,0.05);padding-top:8px;}',
      '#lrb-qa-result b{color:#34d399;}',
      '#lrb-qa-cache-reset{width:100%;margin-top:8px;padding:6px;border-radius:7px;',
      'border:1px solid rgba(245,158,11,0.25);background:rgba(245,158,11,0.08);',
      'color:#f59e0b;font-size:10px;font-weight:700;cursor:pointer;letter-spacing:0.03em;',
      'font-family:monospace;transition:all 0.15s;}',
      '#lrb-qa-cache-reset:hover{background:rgba(245,158,11,0.18);}',
    ].join('');
    document.head.appendChild(style);

    // Build panel HTML
    const panel = document.createElement('div');
    panel.id = 'lrb-qa-panel';

    const header = document.createElement('div');
    header.id = 'lrb-qa-header';
    header.innerHTML =
      '<span>🧪 LRB Signal Tester</span><button id="lrb-qa-toggle" aria-label="Toggle panel">▼</button>';
    panel.appendChild(header);

    const body = document.createElement('div');
    body.id = 'lrb-qa-body';

    // Build chip groups
    const groupDefs = [
      { id: 'pain', label: 'Pain Signals', multi: true, items: SIGNALS.pain },
      { id: 'life_stage', label: 'Life Stage', multi: false, items: SIGNALS.life_stage },
      { id: 'motivation', label: 'Motivation', multi: false, items: SIGNALS.motivation },
      { id: 'tags', label: 'Behavior Tags', multi: true, items: SIGNALS.tags },
    ];

    for (const grp of groupDefs) {
      const wrap = document.createElement('div');
      wrap.className = 'lrb-qa-group';
      const lbl = document.createElement('span');
      lbl.className = 'lrb-qa-group-label';
      lbl.textContent = grp.label;
      wrap.appendChild(lbl);
      const chips = document.createElement('div');
      chips.className = 'lrb-qa-chips';
      for (const item of grp.items) {
        const chip = document.createElement('button');
        chip.className = 'lrb-qa-chip';
        chip.type = 'button';
        chip.textContent = item.label;
        chip.dataset.key = item.key;
        chip.dataset.group = grp.id;
        chip.addEventListener('click', () => {
          if (grp.multi) {
            // Toggle multi
            if (grp.id === 'pain') state.pain[item.key] = state.pain[item.key] ? 0 : 0.9;
            if (grp.id === 'tags') state.tags[item.key] = !state.tags[item.key];
            chip.classList.toggle('active');
          } else {
            // Radio within group
            for (const c of chips.querySelectorAll('.lrb-qa-chip')) c.classList.remove('active');
            chip.classList.add('active');
            if (grp.id === 'life_stage') state.life_stage = item.key;
            if (grp.id === 'motivation') state.motivation = item.key;
          }
        });
        chips.appendChild(chip);
      }
      wrap.appendChild(chips);
      body.appendChild(wrap);
    }

    // Actions
    const actions = document.createElement('div');
    actions.id = 'lrb-qa-actions';
    actions.innerHTML =
      '<button id="lrb-qa-apply" type="button">▶ Apply &amp; Re-render</button>' +
      '<button id="lrb-qa-reset" type="button">Reset</button>';
    body.appendChild(actions);

    // Schema cache reset button
    const CACHE_BTN_LABEL = '🔄 Reset Schema Cache (fix section-add issues)';
    const cacheBtn = document.createElement('button');
    cacheBtn.id = 'lrb-qa-cache-reset';
    cacheBtn.type = 'button';
    cacheBtn.textContent = CACHE_BTN_LABEL;
    body.appendChild(cacheBtn);

    const result = document.createElement('div');
    result.id = 'lrb-qa-result';
    result.style.display = 'none';
    body.appendChild(result);

    panel.appendChild(body);
    document.body.appendChild(panel);

    // Toggle collapse
    let collapsed = false;
    header.addEventListener('click', () => {
      collapsed = !collapsed;
      body.style.display = collapsed ? 'none' : '';
      document.getElementById('lrb-qa-toggle').textContent = collapsed ? '▲' : '▼';
    });

    // Apply button
    const applyBtn = document.getElementById('lrb-qa-apply');
    applyBtn.addEventListener('click', async () => {
      applyBtn.textContent = '⏳ Applying…';
      applyBtn.disabled = true;

      // Build profile payload
      const painMap = Object.fromEntries(Object.entries(state.pain).filter(([, v]) => v > 0));
      const activeTags = Object.keys(state.tags).filter(k => state.tags[k]);

      const extra = {};
      if (Object.keys(painMap).length) extra.pain_points = painMap;
      if (state.life_stage) extra.life_stage = state.life_stage;
      if (state.motivation) extra.motivation_type = state.motivation;
      if (activeTags.length) extra.segment_tags = activeTags;

      // preview: true: gravity-claw returns the overridden profile without
      // saving it, so forced tags never stick to the tester's own visitor record.
      const payload = {
        visitor_id: visitorId,
        page_signals: getPageSignals(),
        preview: true,
        ...extra,
      };

      log('[QA Panel] Applying profile override:', payload);

      try {
        // Upsert profile with forced signals
        const r = await fetch(`${BOT_URL}/admin/intelligence/profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        await r.json();

        // Bust the section cache so a fresh API call fires
        sessionRemove(SECTION_CACHE_KEY);

        // Re-fetch ranked sections with inline profile (bypasses DB lookup)
        const body2 = { visitor_id: visitorId, embed_id: EMBED_ID, profile: extra, preview: true };
        const r2 = await fetch(`${BOT_URL}/admin/intelligence/page/sections`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body2),
        });
        const data = await r2.json();
        const ranked = data.sections || [];
        const host = document.getElementById(HOST_ID);

        // Clear existing injected sections
        if (host) {
          for (const el of host.querySelectorAll('[data-lrb-id]')) host.removeChild(el);

          const pool = document.getElementById(POOL_ID);
          for (const s of ranked.slice(0, MAX_SECTIONS)) {
            injectNow(host, pool, s.shopify_section_id || s.section_id, s.section_id || null);
          }
        }

        // Show result
        result.style.display = 'block';
        result.innerHTML =
          `<b>${ranked.length} section(s) rendered:</b><br>` +
          ranked
            .map(
              s =>
                `• ${s.section_name || s.section_id}` +
                (s.variation_name
                  ? ` <span style="color:#7dd3fc">(${s.variation_name})</span>`
                  : '') +
                ` <span style="color:#64748b">score:${(s.score || 0).toFixed(2)}</span>`
            )
            .join('<br>');

        applyBtn.textContent = '✓ Applied';
        setTimeout(() => {
          applyBtn.textContent = '▶ Apply & Re-render';
          applyBtn.disabled = false;
        }, 2000);
      } catch (e) {
        result.style.display = 'block';
        result.innerHTML = `<span style="color:#f43f5e">Error: ${e.message}</span>`;
        applyBtn.textContent = '▶ Apply & Re-render';
        applyBtn.disabled = false;
      }
    });

    // Reset button
    document.getElementById('lrb-qa-reset').addEventListener('click', () => {
      state = emptyState();
      for (const c of panel.querySelectorAll('.lrb-qa-chip')) c.classList.remove('active');
      result.style.display = 'none';
      sessionRemove(SECTION_CACHE_KEY);
      log('[QA Panel] State reset');
    });

    // Schema cache reset button
    const restoreCacheBtn = delay =>
      setTimeout(() => {
        cacheBtn.textContent = CACHE_BTN_LABEL;
        cacheBtn.style = '';
        cacheBtn.disabled = false;
      }, delay);
    cacheBtn.addEventListener('click', async () => {
      cacheBtn.textContent = '⏳ Reloading schema…';
      cacheBtn.disabled = true;
      try {
        const r = await fetch(`${BOT_URL}/admin/db/reload-schema`, { method: 'POST' });
        const d = await r.json();
        if (d.ok) {
          cacheBtn.textContent = '✅ Schema cache reloaded!';
          cacheBtn.style.borderColor = 'rgba(52,211,153,0.4)';
          cacheBtn.style.background = 'rgba(52,211,153,0.1)';
          cacheBtn.style.color = '#34d399';
        } else {
          cacheBtn.textContent = `⚠️ ${d.error || 'Failed — check Railway logs'}`;
          cacheBtn.style.color = '#f43f5e';
        }
        restoreCacheBtn(4000);
      } catch (e) {
        cacheBtn.textContent = `❌ ${e.message}`;
        cacheBtn.style.color = '#f43f5e';
        restoreCacheBtn(3000);
      }
    });

    log('[QA Panel] Mounted — signal tester active');
  }

  // Wait for lrb-signals.js to check this page before ranking sections, so a
  // signal earned on this very page counts here too. With the rules cached this
  // resolves at once; on a first visit it is bounded by SIGNALS_WAIT_MS.
  // Signals held also key the section cache: a product page cached before the
  // visitor read the weight-limit post must not be replayed after it.
  const SIGNALS_WAIT_MS = 1500;
  let started = false;
  function start() {
    if (started) return;
    started = true;
    const held = window.LRB_SIGNALS ? window.LRB_SIGNALS.tags().slice().sort() : [];
    if (held.length) SECTION_CACHE_KEY += `_s:${held.join('.')}`;
    run();
  }
  function startWhenSignalsReady() {
    if (!window.LRB_SIGNALS) {
      start();
      return;
    }
    setTimeout(start, SIGNALS_WAIT_MS);
    window.LRB_SIGNALS.ready.then(start, start);
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(startWhenSignalsReady);
  // Mount debug panel after DOM is ready
  onReady(buildDebugPanel);
})();
